import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './AuthContext'
import {
  type DraggableWidgetId,
  type FilterableMetricWidgetId,
  type WidgetSettingsMap,
  type WidgetInstanceId,
  DASHBOARD_WIDGETS,
  DEFAULT_DASHBOARD_ENABLED,
  DEFAULT_DASHBOARD_ORDER,
  isFilterableWidget,
  getBaseWidgetId,
  createWidgetInstanceId,
} from '../config/dashboardWidgets'
import { DEFAULT_WIDGET_FILTER, type WidgetFilterSettings } from '../lib/tradeCalculations'

const SETTINGS_KEY = 'dashboard_layout'

export interface DashboardLayoutValue {
  enabled: WidgetInstanceId[]
  order: WidgetInstanceId[]
  widgetSettings: WidgetSettingsMap
  loading: boolean
  saveLayout: (enabled: WidgetInstanceId[], order: WidgetInstanceId[]) => Promise<void>
  getWidgetSettings: (id: WidgetInstanceId) => WidgetFilterSettings
  updateWidgetSettings: (id: WidgetInstanceId, settings: WidgetFilterSettings) => Promise<void>
  isEditMode: boolean
  pendingEnabled: WidgetInstanceId[]
  pendingOrder: WidgetInstanceId[]
  enterEditMode: () => void
  exitEditMode: () => void
  addWidget: (baseId: DraggableWidgetId) => void
  removeWidget: (id: WidgetInstanceId) => void
  reorderWidgets: (newOrder: WidgetInstanceId[]) => void
  saveAndExit: () => Promise<void>
}

const DashboardLayoutContext = createContext<DashboardLayoutValue | undefined>(
  undefined
)

export const useDashboardLayout = () => {
  const context = useContext(DashboardLayoutContext)
  if (context === undefined) {
    throw new Error('useDashboardLayout must be used within a DashboardLayoutProvider')
  }
  return context
}

interface DashboardLayoutProviderProps {
  children: ReactNode
}

const ALL_KNOWN_WIDGET_IDS: DraggableWidgetId[] = [
  'openPositions',
  'totalPnLPercent',
  'totalPnlOpen',
  'totalRiskExposure',
  'tradeList',
  'totalPnl',
  'totalTrades',
  'winRate',
  'profitFactor',
  'avgPnlPercent',
  'weightedPnlPercent',
  'expectancy',
  'averageWin',
  'averageLoss',
  'averageRR',
]

function expandId(id: string): WidgetInstanceId[] {
  if (id === 'topMetrics') {
    return ['openPositions', 'totalPnLPercent', 'totalRiskExposure']
  }
  return [id]
}

export const DashboardLayoutProvider = ({ children }: DashboardLayoutProviderProps) => {
  const { user } = useAuth()
  const [enabled, setEnabled] = useState<WidgetInstanceId[]>(DEFAULT_DASHBOARD_ENABLED)
  const [order, setOrder] = useState<WidgetInstanceId[]>(DEFAULT_DASHBOARD_ORDER)
  const [widgetSettings, setWidgetSettings] = useState<WidgetSettingsMap>({})
  const [loading, setLoading] = useState(true)
  const [isEditMode, setIsEditMode] = useState(false)
  const [pendingEnabled, setPendingEnabled] = useState<WidgetInstanceId[]>(DEFAULT_DASHBOARD_ENABLED)
  const [pendingOrder, setPendingOrder] = useState<WidgetInstanceId[]>(DEFAULT_DASHBOARD_ORDER)

  const fetchLayout = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('value')
        .eq('user_id', userId)
        .eq('key', SETTINGS_KEY)
        .maybeSingle()

      if (error) {
        console.error('Error fetching dashboard layout:', error)
        return
      }

      if (data?.value && typeof data.value === 'object') {
        const v = data.value as {
          enabled?: string[]
          order?: string[]
          widgetSettings?: WidgetSettingsMap
        }
        if (Array.isArray(v.enabled) && v.enabled.length > 0) {
          const migrated: WidgetInstanceId[] = []
          const seen = new Set<WidgetInstanceId>()
          for (const id of v.enabled) {
            for (const w of expandId(id)) {
              if (!seen.has(w)) {
                migrated.push(w)
                seen.add(w)
              }
            }
          }
          if (migrated.length > 0) setEnabled(migrated)
        }
        if (Array.isArray(v.order) && v.order.length > 0) {
          const migrated: WidgetInstanceId[] = []
          const seen = new Set<WidgetInstanceId>()
          for (const id of v.order) {
            for (const w of expandId(id)) {
              if (!seen.has(w)) {
                migrated.push(w)
                seen.add(w)
              }
            }
          }
          if (migrated.length > 0) setOrder(migrated)
        }
        if (v.widgetSettings && typeof v.widgetSettings === 'object') {
          setWidgetSettings(v.widgetSettings)
        }
      }
    } catch (err) {
      console.error('Unexpected error fetching dashboard layout:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!user) {
      setEnabled(DEFAULT_DASHBOARD_ENABLED)
      setOrder(DEFAULT_DASHBOARD_ORDER)
      setWidgetSettings({})
      setLoading(false)
      return
    }
    setLoading(true)
    fetchLayout(user.id)
  }, [user?.id, fetchLayout])

  useEffect(() => {
    setPendingEnabled(enabled)
    setPendingOrder(order)
  }, [enabled, order])

  const enterEditMode = useCallback(() => {
    setPendingEnabled(enabled)
    setPendingOrder(order)
    setIsEditMode(true)
  }, [enabled, order])

  const exitEditMode = useCallback(() => setIsEditMode(false), [])

  const addWidget = useCallback((baseId: DraggableWidgetId) => {
    const instanceId = createWidgetInstanceId(baseId)
    if (baseId === 'tradeList') {
      const hasTradeList = (arr: WidgetInstanceId[]) =>
        arr.some((id) => getBaseWidgetId(id) === 'tradeList')
      setPendingEnabled((prev) => (hasTradeList(prev) ? prev : [...prev, instanceId]))
      setPendingOrder((prev) => {
        if (hasTradeList(prev)) return prev
        return [...prev, instanceId]
      })
    } else {
      setPendingEnabled((prev) => [...prev, instanceId])
      setPendingOrder((prev) => {
        const config = DASHBOARD_WIDGETS[baseId]
        const is1x1 = config && config.width === 1 && config.height === 1
        if (is1x1) {
          const tradeListIndex = prev.findIndex((id) => getBaseWidgetId(id) === 'tradeList')
          if (tradeListIndex >= 0) {
            return [...prev.slice(0, tradeListIndex), instanceId, ...prev.slice(tradeListIndex)]
          }
        }
        return [...prev, instanceId]
      })
    }
  }, [])

  const removeWidget = useCallback((id: DraggableWidgetId) => {
    setPendingEnabled((prev) => prev.filter((x) => x !== id))
    setPendingOrder((prev) => prev.filter((x) => x !== id))
  }, [])

  const reorderWidgets = useCallback((newOrder: WidgetInstanceId[]) => {
    setPendingOrder(newOrder)
  }, [])

  const getWidgetSettings = useCallback(
    (id: WidgetInstanceId): WidgetFilterSettings => {
      if (!isFilterableWidget(id)) return DEFAULT_WIDGET_FILTER
      return { ...DEFAULT_WIDGET_FILTER, ...widgetSettings[id] }
    },
    [widgetSettings]
  )

  const updateWidgetSettings = useCallback(
    async (id: WidgetInstanceId, settings: WidgetFilterSettings) => {
      if (!user) return
      const next = { ...widgetSettings, [id]: settings }
      setWidgetSettings(next)
      try {
        const { error } = await supabase.from('user_settings').upsert(
          {
            user_id: user.id,
            key: SETTINGS_KEY,
            value: { enabled, order, widgetSettings: next },
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,key' }
        )
        if (error) throw error
      } catch (err) {
        console.error('Error saving widget settings:', err)
        setWidgetSettings(widgetSettings)
        throw err
      }
    },
    [user, enabled, order, widgetSettings]
  )

  const saveLayout = useCallback(
    async (newEnabled: WidgetInstanceId[], newOrder: WidgetInstanceId[]) => {
      if (!user) return

      const value = { enabled: newEnabled, order: newOrder, widgetSettings }

      try {
        const { error } = await supabase.from('user_settings').upsert(
          {
            user_id: user.id,
            key: SETTINGS_KEY,
            value,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,key' }
        )

        if (error) throw error

        setEnabled(newEnabled)
        setOrder(newOrder)
      } catch (err) {
        console.error('Error saving dashboard layout:', err)
        throw err
      }
    },
    [user, widgetSettings]
  )

  const saveAndExit = useCallback(async () => {
    const newOrder = pendingOrder.filter((id) => pendingEnabled.includes(id))
    await saveLayout(newOrder, newOrder)
    setIsEditMode(false)
  }, [pendingEnabled, pendingOrder, saveLayout])

  const value: DashboardLayoutValue = {
    enabled,
    order,
    widgetSettings,
    loading,
    saveLayout,
    getWidgetSettings,
    updateWidgetSettings,
    isEditMode,
    pendingEnabled,
    pendingOrder,
    enterEditMode,
    exitEditMode,
    addWidget,
    removeWidget,
    reorderWidgets,
    saveAndExit,
  }

  return (
    <DashboardLayoutContext.Provider value={value}>
      {children}
    </DashboardLayoutContext.Provider>
  )
}
